/**
 * First blog post: TELC B1 Redemittel Schreiben & Preparation.
 * Renders the full article body (intro, table, sections, quote, FAQ).
 * FAQ uses same accordion layout as the main FAQ page.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'How long is a TELC B1 certificate valid?',
    answer: 'Unlike some language certifications, your b1 exam certificate is valid indefinitely. However, some employers or academic institutions may request a certificate no older than 2 years to ensure current proficiency.',
  },
  {
    question: 'Can I use the same Redemittel for the oral exam?',
    answer: 'Yes. Many structural phrases used to express your opinion or structure a presentation overlap perfectly between the schreiben (writing) and oral examination sections.',
  },
  {
    question: 'What is the best study tip for the writing task?',
    answer: 'Stop relying purely on a downloaded phrase list. The best tip is to practice writing a complete letter under timed conditions using a digital simulator to build real exam endurance.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const TelcB1RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        Redemittel are the fixed phrases that carry an exam answer: how you open a letter, give your opinion, make a suggestion or end a conversation politely. In telc Deutsch B1 they count in two places: the <em>Schriftlicher Ausdruck</em>, one letter in 30 minutes, and the <em>Mündlicher Ausdruck</em>, about 15 minutes of speaking with a partner. Below are the phrases sorted by exam task, with English meanings and example sentences, and the complete list is a free PDF.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        What Are the Essential Redemittel TELC B1 Schreiben and Sprechen Phrases?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The essential <em>redemittel telc b1</em> phrases provide the structural framework for writing a formal letter or
        email. Memorizing specific vocabulary for opening, expressing a <em>meinung</em> (opinion), asking a{' '}
        <em>frage</em> (question), and closing ensures you meet the strict grading criteria.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        For example, starting your letter with &quot;Sehr geehrte Damen und Herren&quot; immediately establishes the
        correct formal tone required for the <em>schreiben</em> task. Rather than spending 2 years studying unstructured
        vocabulary, candidates should focus on high-yield phrases. When practicing, use these structural elements to
        formulate a coherent answer regardless of the specific topic. Transitioning these language skills to higher-level
        certifications like B2 and C1 becomes significantly easier when the B1 foundation is perfectly structured.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 12 complete telc B1 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          If you want the complete collection, you can download the PDF for the full Redemittel with examples and
          exercises.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full telc B1 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="telc"
        level="b1"
        examName="telc B1"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <SprechenSamplesCallout provider="telc" level="b1" />
      <RelatedResources provider="telc" level="b1" variant="redemittel" />
    </article>
  );
};

export default TelcB1RedemittelPost;
